/**
 * Painel GM — Streamers Oficiais.
 *
 * Tela unica com tabs (padrao GuardPage):
 *   Streamers · Comissoes · Sessoes · Cupons · Credenciais
 *
 * Estetica STREAMER/Twitch: acentos roxo/neon (#9146FF twitch purple + magenta
 * + ciano) sobre o fundo escuro do admin, containers quadradinhos (rounded-lg),
 * glows discretos. Continua legivel/pro — e' area de gestao.
 *
 * "Ver Painel" abre um modal com o status da live + embeds (player + chat) da
 * plataforma conectada, montados client-side, e faz poll do getStreamerLive a
 * cada ~30s enquanto aberto.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Tv, Loader2, AlertCircle, RefreshCw, Plus, Pencil, X, Check, Ban,
  DollarSign, Radio, Clock, Ticket, KeyRound, Eye, EyeOff, Users,
  ExternalLink, Trash2, CheckCircle2, Copy, Info, ChevronDown,
} from 'lucide-react';
import {
  listStreamers, createStreamer, updateStreamer, deleteStreamer,
  getStreamerDetail, listStreamerCommissions, payStreamerCommission,
  listStreamerSessions, getStreamerLive, listStreamerCoupons,
  listStreamerCredentials, updateStreamerCredential,
  type StreamerRow, type StreamerStatus, type StreamerDetail,
  type StreamerCommission, type StreamerSession, type StreamerLive,
  type StreamerCredential, type StreamerPlatform, type CommissionStatus,
  type StreamerCouponRow,
} from '@/shared/services/adminService';
import { useToast } from '@/shared/contexts/ToastContext';
import { DataTable, type DataTableColumn } from '@/shared/components/ui/DataTable';
import { RowActions } from '@/shared/components/ui/RowActions';
import { useTableSort } from '@/shared/hooks/useTableSort';

/* Twitch purple + neon magenta/cyan — o vocabulario de cor da area streamer. */
const NEON = {
  purple: '#9146FF',
  magenta: '#ff3df0',
  cyan: '#00e1d7',
};

const PAGE_SIZE = 25;
const LIVE_POLL_MS = 30000;

type Tab = 'streamers' | 'commissions' | 'sessions' | 'coupons' | 'credentials';

const brl = (v?: number | null) =>
  (v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const dt = (v?: string | null) => (v ? new Date(v).toLocaleString('pt-BR') : '—');

const fmtDuration = (sec?: number | null) => {
  if (!sec || sec <= 0) return '—';
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  return h > 0 ? `${h}h ${m}m` : m > 0 ? `${m}m ${s}s` : `${s}s`;
};

/* ═══════════════════════════════════════════════════════ Wrapper ═════════ */

export default function StreamersGmPage() {
  const [tab, setTab] = useState<Tab>('streamers');

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl md:text-3xl font-bold text-white flex items-center gap-3">
          <span
            className="w-11 h-11 rounded-lg flex items-center justify-center border"
            style={{
              background: `${NEON.purple}18`,
              borderColor: `${NEON.purple}55`,
              color: NEON.purple,
              boxShadow: `0 0 22px ${NEON.purple}33`,
            }}
          >
            <Tv size={22} />
          </span>
          Streamers
        </h1>
        <p className="text-sm text-white/65 mt-1">
          Plataforma de Streamers Oficiais — cadastro, cupons, comissões, sessões live e credenciais das plataformas.
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-5 surface-card rounded-lg p-1 w-fit overflow-x-auto">
        <TabBtn active={tab === 'streamers'} onClick={() => setTab('streamers')}>
          <Tv size={14} /> Streamers
        </TabBtn>
        <TabBtn active={tab === 'commissions'} onClick={() => setTab('commissions')}>
          <DollarSign size={14} /> Comissões
        </TabBtn>
        <TabBtn active={tab === 'sessions'} onClick={() => setTab('sessions')}>
          <Clock size={14} /> Sessões
        </TabBtn>
        <TabBtn active={tab === 'coupons'} onClick={() => setTab('coupons')}>
          <Ticket size={14} /> Cupons
        </TabBtn>
        <TabBtn active={tab === 'credentials'} onClick={() => setTab('credentials')}>
          <KeyRound size={14} /> Credenciais
        </TabBtn>
      </div>

      {tab === 'streamers' && <StreamersTab />}
      {tab === 'commissions' && <CommissionsTab />}
      {tab === 'sessions' && <SessionsTab />}
      {tab === 'coupons' && <CouponsTab />}
      {tab === 'credentials' && <CredentialsTab />}
    </div>
  );
}

function TabBtn({ active, onClick, children }: {
  active: boolean; onClick: () => void; children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition cursor-pointer whitespace-nowrap ${
        active ? '' : 'text-white/55 hover:text-white hover:bg-white/[0.03]'
      }`}
      style={active ? { background: `${NEON.purple}22`, color: '#c9a9ff' } : undefined}
    >
      {children}
    </button>
  );
}

/* ─────────────────────────────────────────────────────── status badge ──── */

const STATUS_META: Record<StreamerStatus, { label: string; color: string }> = {
  pending:   { label: 'Pendente',  color: '#f59e0b' },
  active:    { label: 'Ativo',     color: '#10b981' },
  inactive:  { label: 'Inativo',   color: '#8a94a3' },
  suspended: { label: 'Suspenso',  color: '#fa3a3a' },
};

function StatusBadge({ status }: { status: StreamerStatus }) {
  const m = STATUS_META[status] ?? STATUS_META.inactive;
  return (
    <span
      className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full border"
      style={{ background: `${m.color}18`, borderColor: `${m.color}44`, color: m.color }}
    >
      {m.label}
    </span>
  );
}

function Avatar({ nick, accountId, avatar }: {
  nick?: string | null; accountId?: string | null; avatar?: string | null;
}) {
  const name = nick || accountId || '?';
  if (avatar) {
    return (
      <img
        src={avatar}
        alt={name}
        className="w-8 h-8 rounded-lg object-cover border"
        style={{ borderColor: `${NEON.purple}44` }}
      />
    );
  }
  return (
    <div
      className="w-8 h-8 rounded-lg flex items-center justify-center text-xs font-black shrink-0 border"
      style={{ background: `${NEON.purple}20`, borderColor: `${NEON.purple}44`, color: '#c9a9ff' }}
    >
      {name[0]?.toUpperCase() || '?'}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════ STREAMERS ═══════ */

function StreamersTab() {
  const { notify } = useToast();
  const [items, setItems] = useState<StreamerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState<StreamerRow | null>(null);
  const [panelId, setPanelId] = useState<number | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const { sorted, sort, toggle } = useTableSort(items, { key: 'createdAt', order: 'desc' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try { setItems(await listStreamers()); }
    catch (err: any) { setError(err?.detail || err?.message || 'Erro'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const patchStatus = async (s: StreamerRow, status: StreamerStatus, msg: string) => {
    setBusyId(s.id);
    try {
      await updateStreamer(s.id, { status });
      notify('success', msg);
      await fetchData();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    } finally { setBusyId(null); }
  };

  const onDelete = async (s: StreamerRow) => {
    if (!confirm(`Remover o streamer ${s.nick || s.accountId || s.userNum}?`)) return;
    setBusyId(s.id);
    try {
      await deleteStreamer(s.id);
      notify('success', 'Streamer removido');
      await fetchData();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    } finally { setBusyId(null); }
  };

  const cols: DataTableColumn<StreamerRow>[] = [
    { key: 'nick', label: 'Streamer', width: '1.8fr', sortKey: 'nick',
      cell: (s) => (
        <div className="flex items-center gap-2.5 min-w-0">
          <Avatar nick={s.nick} accountId={s.accountId} avatar={s.avatar} />
          <div className="min-w-0">
            <p className="text-sm font-bold text-white truncate">{s.nick || s.accountId || `#${s.userNum}`}</p>
            <p className="text-[10px] text-white/40 font-mono">UN {s.userNum}</p>
          </div>
        </div>
      ) },
    { key: 'accountId', label: 'Conta', width: '1.2fr', sortKey: 'accountId',
      cell: (s) => <span className="text-xs text-white/75 truncate">{s.accountId || '—'}</span> },
    { key: 'status', label: 'Status', width: '110px', align: 'center', sortKey: 'status',
      cell: (s) => <StatusBadge status={s.status} /> },
    { key: 'commissionPct', label: 'Comissão', width: '100px', align: 'center', sortKey: 'commissionPct',
      cell: (s) => <span className="text-sm font-bold" style={{ color: NEON.magenta }}>{s.commissionPct ?? 0}%</span> },
    { key: 'createdAt', label: 'Cadastro', width: '160px', sortKey: 'createdAt',
      cell: (s) => <span className="text-[11px] text-white/55 font-mono">{dt(s.createdAt)}</span> },
    { key: 'actions', label: 'Ações', width: '60px', align: 'right', mobileHidden: true,
      cell: (s) => rowActions(s) },
  ];

  const rowActions = (s: StreamerRow) => (
    <RowActions
      actions={[
        { label: 'Ver Painel', icon: <Radio size={13} />, onClick: () => setPanelId(s.id), variant: 'primary' },
        ...(s.status !== 'active'
          ? [{ label: 'Aprovar', icon: <Check size={13} />, variant: 'success' as const,
               onClick: () => patchStatus(s, 'active', 'Streamer aprovado'), disabled: busyId === s.id }]
          : []),
        ...(s.status !== 'suspended'
          ? [{ label: 'Suspender', icon: <Ban size={13} />, variant: 'danger' as const,
               onClick: () => patchStatus(s, 'suspended', 'Streamer suspenso'), disabled: busyId === s.id }]
          : []),
        ...(s.status !== 'inactive'
          ? [{ label: 'Inativar', icon: <X size={13} />, variant: 'default' as const,
               onClick: () => patchStatus(s, 'inactive', 'Streamer inativado'), disabled: busyId === s.id }]
          : []),
        { label: 'Editar', icon: <Pencil size={13} />, onClick: () => setEditing(s), variant: 'warning' },
        { label: 'Remover', icon: <Trash2 size={13} />, onClick: () => onDelete(s), variant: 'danger', disabled: busyId === s.id },
      ]}
    />
  );

  const activeCount = items.filter(s => s.status === 'active').length;
  const pendingCount = items.filter(s => s.status === 'pending').length;

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <p className="text-sm text-white/65">
          {items.length} streamer{items.length !== 1 && 's'} ·{' '}
          <span style={{ color: STATUS_META.active.color }}>{activeCount} ativo{activeCount !== 1 && 's'}</span>
          {pendingCount > 0 && (
            <> · <span style={{ color: STATUS_META.pending.color }}>{pendingCount} pendente{pendingCount !== 1 && 's'}</span></>
          )}
        </p>
        <div className="flex gap-2">
          <button onClick={fetchData} disabled={loading}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold
                       bg-white/[0.04] hover:bg-white/[0.08] text-white/75 transition cursor-pointer">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
          <NeonButton onClick={() => setCreating(true)}>
            <Plus size={14} /> Novo Streamer
          </NeonButton>
        </div>
      </div>

      {error && <ErrorBox msg={error} />}

      <DataTable<StreamerRow>
        columns={cols}
        rows={sorted}
        sort={sort}
        onSort={toggle}
        rowKey={(s) => s.id}
        loading={loading}
        empty="Nenhum streamer cadastrado"
        mobileTitle={(s) => (
          <span className="flex items-center gap-2">
            {s.nick || s.accountId || `#${s.userNum}`} <StatusBadge status={s.status} />
          </span>
        )}
        mobileSubtitle={(s) => `Conta ${s.accountId || '—'} · ${s.commissionPct ?? 0}% comissão`}
        mobileActions={(s) => rowActions(s)}
      />

      {/* Grade das lives em tempo real (muted), so aparece se tiver alguem ao vivo */}
      <LiveNowGrid streamers={items} />

      {(creating || editing) && (
        <StreamerEditor
          streamer={editing}
          onClose={() => { setCreating(false); setEditing(null); }}
          onSaved={async () => { await fetchData(); setCreating(false); setEditing(null); }}
        />
      )}

      {panelId != null && (
        <StreamerPanelModal id={panelId} onClose={() => setPanelId(null)} />
      )}
    </div>
  );
}

/* ─────────────────────────────────────────── criar / editar streamer ──── */

function StreamerEditor({ streamer, onClose, onSaved }: {
  streamer: StreamerRow | null; onClose: () => void; onSaved: () => Promise<void>;
}) {
  const { notify } = useToast();
  const isEdit = !!streamer;
  const [userNum, setUserNum] = useState(String(streamer?.userNum ?? ''));
  const [nick, setNick] = useState(streamer?.nick ?? '');
  const [commissionPct, setCommissionPct] = useState(String(streamer?.commissionPct ?? 5));
  const [notes, setNotes] = useState(streamer?.notes ?? '');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    const pct = Math.min(100, Math.max(0, parseInt(commissionPct) || 0));
    setBusy(true);
    try {
      if (isEdit && streamer) {
        await updateStreamer(streamer.id, {
          nick: nick.trim() || undefined,
          commissionPct: pct,
          notes: notes.trim() || undefined,
        });
        notify('success', 'Streamer atualizado');
      } else {
        const un = parseInt(userNum);
        if (!un) { notify('error', 'UserNum inválido'); setBusy(false); return; }
        await createStreamer({ userNum: un, nick: nick.trim() || undefined, commissionPct: pct });
        notify('success', 'Streamer criado (pendente)');
      }
      await onSaved();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    } finally { setBusy(false); }
  };

  return (
    <NeonModal onClose={onClose} maxW="480px" title={isEdit ? 'Editar Streamer' : 'Novo Streamer'} icon={<Tv size={18} />}>
      <div className="space-y-4">
        <div>
          <label className="text-[11px] text-white/55 mb-1 block">UserNum</label>
          <input
            type="text" inputMode="numeric" value={userNum} disabled={isEdit}
            onChange={(e) => setUserNum(e.target.value.replace(/\D/g, ''))}
            placeholder="ex: 4360"
            className="w-full bg-[#0d141b] border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white font-mono
                       outline-none focus:border-[#9146FF]/50 disabled:opacity-50"
          />
          {isEdit && <p className="text-[10px] text-white/40 mt-1">Conta não pode ser alterada após o cadastro.</p>}
        </div>
        <div>
          <label className="text-[11px] text-white/55 mb-1 block">Nick / Nome do streamer</label>
          <input
            type="text" value={nick}
            onChange={(e) => setNick(e.target.value)}
            placeholder="ex: LibertyGamer"
            className="w-full bg-[#0d141b] border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white
                       outline-none focus:border-[#9146FF]/50"
          />
        </div>
        <div>
          <label className="text-[11px] text-white/55 mb-1 block">Comissão (%)</label>
          <input
            type="text" inputMode="numeric" value={commissionPct}
            onChange={(e) => setCommissionPct(e.target.value.replace(/\D/g, '').slice(0, 3))}
            placeholder="5"
            className="w-full bg-[#0d141b] border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white font-mono
                       outline-none focus:border-[#9146FF]/50"
          />
          <p className="text-[10px] text-white/40 mt-1">Padrão 5% sobre o valor bruto das recargas via cupom.</p>
        </div>
        {isEdit && (
          <div>
            <label className="text-[11px] text-white/55 mb-1 block">Notas (interno)</label>
            <textarea
              value={notes} rows={2}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full bg-[#0d141b] border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white resize-none
                         outline-none focus:border-[#9146FF]/50"
            />
          </div>
        )}
        <div className="flex gap-2 pt-1">
          <button onClick={onClose}
            className="flex-1 px-4 py-2.5 rounded-lg bg-white/[0.04] text-white/75 hover:bg-white/[0.08] transition cursor-pointer text-sm">
            Cancelar
          </button>
          <NeonButton onClick={save} disabled={busy} className="flex-1 justify-center">
            {busy && <Loader2 size={14} className="animate-spin" />}
            Salvar
          </NeonButton>
        </div>
      </div>
    </NeonModal>
  );
}

/* ─────────────────────────────────────────────── modal "Ver Painel" ──── */

function buildEmbeds(live: StreamerLive, hostname: string): { player?: string; chat?: string } {
  const conn = live.connection;
  if (!conn) return {};
  const platform = String(conn.platform || '').toLowerCase();
  const channelId = conn.channelId || '';
  const videoId = live.videoId || '';

  if (platform === 'twitch') {
    return {
      player: channelId
        ? `https://player.twitch.tv/?channel=${encodeURIComponent(channelId)}&parent=${hostname}`
        : undefined,
      chat: channelId
        ? `https://www.twitch.tv/embed/${encodeURIComponent(channelId)}/chat?parent=${hostname}`
        : undefined,
    };
  }
  if (platform === 'youtube') {
    const player = videoId
      ? `https://www.youtube.com/embed/${encodeURIComponent(videoId)}`
      : channelId
        ? `https://www.youtube.com/embed/live_stream?channel=${encodeURIComponent(channelId)}`
        : undefined;
    return {
      player,
      chat: videoId
        ? `https://www.youtube.com/live_chat?v=${encodeURIComponent(videoId)}&embed_domain=${hostname}`
        : undefined,
    };
  }
  if (platform === 'kick') {
    return {
      player: channelId ? `https://player.kick.com/${encodeURIComponent(channelId)}` : undefined,
      chat: channelId ? `https://kick.com/${encodeURIComponent(channelId)}/chatroom` : undefined,
    };
  }
  if (platform === 'tiktok') {
    return {
      player: channelId ? `https://www.tiktok.com/embed/live/@${encodeURIComponent(channelId)}` : undefined,
    };
  }
  return {};
}

/* Player MUTADO (autoplay) — usado na grade "Ao vivo agora" pra nao vazar audio. */
function buildMutedPlayer(live: StreamerLive, hostname: string): string | undefined {
  const conn = live.connection;
  if (!conn) return undefined;
  const platform = String(conn.platform || '').toLowerCase();
  const channelId = conn.channelId || '';
  const videoId = live.videoId || '';
  if (platform === 'twitch') {
    return channelId
      ? `https://player.twitch.tv/?channel=${encodeURIComponent(channelId)}&parent=${hostname}&muted=true&autoplay=true`
      : undefined;
  }
  if (platform === 'youtube') {
    if (videoId) return `https://www.youtube.com/embed/${encodeURIComponent(videoId)}?autoplay=1&mute=1`;
    if (channelId) return `https://www.youtube.com/embed/live_stream?channel=${encodeURIComponent(channelId)}&autoplay=1&mute=1`;
    return undefined;
  }
  if (platform === 'kick') {
    return channelId ? `https://player.kick.com/${encodeURIComponent(channelId)}?muted=true&autoplay=true` : undefined;
  }
  if (platform === 'tiktok') {
    return channelId ? `https://www.tiktok.com/embed/live/@${encodeURIComponent(channelId)}` : undefined;
  }
  return undefined;
}

/* Grade "Ao vivo agora" — todas as lives rolando, MUTADAS, atualiza a cada 45s.
   So aparece se tiver alguem ao vivo. Fica embaixo da lista de streamers. */
function LiveNowGrid({ streamers }: { streamers: StreamerRow[] }) {
  const [lives, setLives] = useState<Record<number, StreamerLive>>({});
  const [checked, setChecked] = useState(false);
  const hostname = typeof window !== 'undefined' ? window.location.hostname : 'localhost';

  const ids = useMemo(() => streamers.map(s => s.id).join(','), [streamers]);

  const refresh = useCallback(async () => {
    if (streamers.length === 0) { setLives({}); setChecked(true); return; }
    const results = await Promise.all(
      streamers.map(s =>
        getStreamerLive(s.id).then(l => [s.id, l] as const).catch(() => [s.id, null] as const),
      ),
    );
    const map: Record<number, StreamerLive> = {};
    for (const [id, l] of results) if (l) map[id] = l;
    setLives(map);
    setChecked(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ids]);

  useEffect(() => { setChecked(false); refresh(); }, [refresh]);
  useEffect(() => {
    const t = setInterval(refresh, 45000);
    return () => clearInterval(t);
  }, [refresh]);

  const liveOnes = streamers
    .map(s => ({ s, live: lives[s.id] }))
    .filter(x => x.live?.live);

  if (!checked || liveOnes.length === 0) return null;  // nada ao vivo -> some

  return (
    <div className="mt-8 pt-6 border-t border-white/[0.06]">
      <h3 className="text-sm font-black uppercase tracking-wider mb-1 flex items-center gap-2" style={{ color: '#c9a9ff' }}>
        <Radio size={15} className="text-red-500 animate-pulse" /> Ao vivo agora ({liveOnes.length})
      </h3>
      <p className="text-[11px] text-white/45 mb-4">Todas as transmissões em tempo real — áudio mutado pra não sobrepor.</p>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        {liveOnes.map(({ s, live }) => {
          const player = live ? buildMutedPlayer(live, hostname) : undefined;
          return (
            <div key={s.id} className="rounded-lg overflow-hidden border"
              style={{ borderColor: 'rgba(239,58,58,0.4)', boxShadow: '0 0 18px rgba(239,58,58,0.14)' }}>
              <div className="flex items-center justify-between px-3 py-2 bg-[#0d0714]">
                <span className="text-sm font-bold text-white truncate flex items-center gap-2 min-w-0">
                  <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse shrink-0" />
                  <span className="truncate">{s.nick || s.accountId || `#${s.userNum}`}</span>
                  {live?.connection?.platform && (
                    <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded shrink-0"
                      style={{ background: `${NEON.purple}22`, color: '#c9a9ff' }}>
                      {String(live.connection.platform)}
                    </span>
                  )}
                </span>
                {live?.viewers != null && (
                  <span className="inline-flex items-center gap-1 text-xs text-white/70 shrink-0">
                    <Users size={12} style={{ color: NEON.cyan }} /> {live.viewers.toLocaleString('pt-BR')}
                  </span>
                )}
              </div>
              {player ? (
                <div className="bg-black aspect-video">
                  <iframe title={`live-${s.id}`} src={player} allowFullScreen className="w-full h-full" style={{ border: 0 }} />
                </div>
              ) : (
                <div className="bg-[#0d141b] aspect-video flex items-center justify-center text-white/35 text-xs">
                  Player indisponível ({String(live?.connection?.platform || '—')})
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StreamerPanelModal({ id, onClose }: { id: number; onClose: () => void }) {
  const { notify } = useToast();
  const [detail, setDetail] = useState<StreamerDetail | null>(null);
  const [live, setLive] = useState<StreamerLive | null>(null);
  const [loading, setLoading] = useState(true);
  const hostname = typeof window !== 'undefined' ? window.location.hostname : 'localhost';

  const fetchLive = useCallback(async () => {
    try { setLive(await getStreamerLive(id)); } catch { /* silencioso no poll */ }
  }, [id]);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [d, l] = await Promise.all([
          getStreamerDetail(id),
          getStreamerLive(id).catch(() => null),
        ]);
        if (!alive) return;
        setDetail(d);
        setLive(l);
      } catch (err: any) {
        if (alive) notify('error', err?.detail || err?.message || 'Erro ao carregar painel');
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [id, notify]);

  // Poll da live a cada 30s enquanto o modal esta aberto.
  useEffect(() => {
    const t = setInterval(fetchLive, LIVE_POLL_MS);
    return () => clearInterval(t);
  }, [fetchLive]);

  const s = detail?.streamer;
  const embeds = live ? buildEmbeds(live, hostname) : {};
  const isLive = !!live?.live;

  return (
    <NeonModal onClose={onClose} maxW="1000px" title={s?.nick || s?.accountId || `Streamer #${id}`} icon={<Radio size={18} />}>
      {loading ? (
        <div className="py-16 flex items-center justify-center text-white/40">
          <Loader2 size={20} className="animate-spin" />
        </div>
      ) : (
        <div className="space-y-5">
          {/* Cabecalho live */}
          <div className="flex flex-wrap items-center gap-3">
            <span
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-black uppercase tracking-wider border"
              style={isLive
                ? { background: '#fa3a3a18', borderColor: '#fa3a3a55', color: '#ff5a5a', boxShadow: '0 0 18px #fa3a3a44' }
                : { background: 'rgba(255,255,255,0.04)', borderColor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.5)' }}
            >
              <span className={`w-2 h-2 rounded-full ${isLive ? 'bg-red-500 animate-pulse' : 'bg-white/30'}`} />
              {isLive ? 'AO VIVO' : 'OFFLINE'}
            </span>
            {isLive && live?.viewers != null && (
              <span className="inline-flex items-center gap-1.5 text-sm font-bold text-white/85">
                <Users size={14} style={{ color: NEON.cyan }} />
                {live.viewers.toLocaleString('pt-BR')} espectadores
              </span>
            )}
            {live?.connection?.platform && (
              <span
                className="text-[10px] font-black uppercase tracking-wider px-2 py-1 rounded-md border"
                style={{ background: `${NEON.purple}18`, borderColor: `${NEON.purple}44`, color: '#c9a9ff' }}
              >
                {String(live.connection.platform)}
              </span>
            )}
            {live?.connection?.channelUrl && (
              <a href={live.connection.channelUrl} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs text-white/60 hover:text-white transition">
                <ExternalLink size={12} /> Abrir canal
              </a>
            )}
          </div>

          {live?.title && (
            <p className="text-sm text-white/85 font-semibold">{live.title}</p>
          )}

          {/* Embeds player + chat */}
          {embeds.player || embeds.chat ? (
            <div className="grid grid-cols-1 lg:grid-cols-[1fr_340px] gap-3">
              {embeds.player ? (
                <div className="rounded-lg overflow-hidden border border-white/[0.08] bg-black aspect-video">
                  <iframe
                    title="stream-player"
                    src={embeds.player}
                    allowFullScreen
                    className="w-full h-full"
                    style={{ border: 0 }}
                  />
                </div>
              ) : (
                <div className="rounded-lg border border-white/[0.08] bg-[#0d141b] aspect-video flex items-center justify-center text-white/35 text-sm">
                  Player indisponível para esta plataforma
                </div>
              )}
              {embeds.chat ? (
                <div className="rounded-lg overflow-hidden border border-white/[0.08] bg-[#0d141b] h-[300px] lg:h-auto">
                  <iframe
                    title="stream-chat"
                    src={embeds.chat}
                    className="w-full h-full"
                    style={{ border: 0, minHeight: 300 }}
                  />
                </div>
              ) : (
                <div className="rounded-lg border border-white/[0.08] bg-[#0d141b] p-4 text-white/35 text-xs flex items-center justify-center">
                  Chat não disponível nesta plataforma.
                </div>
              )}
            </div>
          ) : (
            <div className="rounded-lg border border-white/[0.08] bg-[#0d141b] p-8 text-center text-white/40 text-sm">
              Sem conexão de plataforma configurada para exibir o embed.
            </div>
          )}

          {/* Conexoes + redes sociais */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <Panel title="Conexões de plataforma">
              {detail && detail.connections.length > 0 ? (
                <div className="space-y-1.5">
                  {detail.connections.map((c, i) => (
                    <div key={c.id ?? i} className="flex items-center gap-2 text-xs">
                      <span className="text-[10px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded"
                        style={{ background: `${NEON.purple}18`, color: '#c9a9ff' }}>
                        {c.platform}
                      </span>
                      <span className="text-white/70 truncate flex-1 font-mono">{c.channelId || '—'}</span>
                      {c.verified && <Check size={12} className="text-emerald-400 shrink-0" />}
                      {c.channelUrl && (
                        <a href={c.channelUrl} target="_blank" rel="noreferrer" className="text-white/45 hover:text-white">
                          <ExternalLink size={12} />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              ) : <Empty>Sem conexões</Empty>}
            </Panel>
            <Panel title="Redes sociais">
              {detail && detail.socials.length > 0 ? (
                <div className="space-y-1.5">
                  {detail.socials.map((soc, i) => (
                    <a key={soc.id ?? i} href={soc.url} target="_blank" rel="noreferrer"
                      className="flex items-center gap-2 text-xs text-white/70 hover:text-white transition">
                      <ExternalLink size={12} className="shrink-0" style={{ color: NEON.magenta }} />
                      <span className="truncate">{soc.network ? `${soc.network} · ` : ''}{soc.url}</span>
                    </a>
                  ))}
                </div>
              ) : <Empty>Sem redes cadastradas</Empty>}
            </Panel>
          </div>
        </div>
      )}
    </NeonModal>
  );
}

/* ═══════════════════════════════════════════════════════ COMISSÕES ═══════ */

const COMMISSION_STATUS_META: Record<CommissionStatus, { label: string; color: string }> = {
  pendente:  { label: 'Pendente',  color: '#f59e0b' },
  pago:      { label: 'Pago',      color: '#10b981' },
  cancelado: { label: 'Cancelado', color: '#8a94a3' },
};

function CommissionsTab() {
  const { notify } = useToast();
  const [items, setItems] = useState<StreamerCommission[]>([]);
  const [total, setTotal] = useState(0);
  const [totals, setTotals] = useState({ paid: 0, pending: 0, cancelled: 0 });
  const [status, setStatus] = useState<CommissionStatus | ''>('');
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);

  const { sorted, sort, toggle } = useTableSort(items, { key: 'createdAt', order: 'desc' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listStreamerCommissions({
        status: status || undefined,
        skip: page * PAGE_SIZE,
        limit: PAGE_SIZE,
      });
      setItems(res.items);
      setTotal(res.total);
      setTotals(res.totals);
    } catch (err: any) {
      setError(err?.detail || err?.message || 'Erro');
    } finally { setLoading(false); }
  }, [status, page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const onPay = async (c: StreamerCommission) => {
    setBusyId(c.id);
    try {
      await payStreamerCommission(c.id);
      notify('success', 'Comissão marcada como paga');
      await fetchData();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    } finally { setBusyId(null); }
  };

  const cols: DataTableColumn<StreamerCommission>[] = [
    { key: 'nick', label: 'Streamer', width: '1.4fr', sortKey: 'nick',
      cell: (c) => (
        <div className="min-w-0">
          <p className="text-sm font-bold text-white truncate">{c.nick || `#${c.streamerId}`}</p>
          <p className="text-[10px] text-white/40 truncate">{c.accountId || '—'}</p>
        </div>
      ) },
    { key: 'brl', label: 'Recarga', width: '110px', align: 'right', sortKey: 'brl',
      cell: (c) => <span className="text-sm text-white/85 font-mono">{brl(c.brl)}</span> },
    { key: 'commissionBrl', label: 'Comissão', width: '110px', align: 'right', sortKey: 'commissionBrl',
      cell: (c) => <span className="text-sm font-bold font-mono" style={{ color: NEON.magenta }}>{brl(c.commissionBrl)}</span> },
    { key: 'commissionStatus', label: 'Status', width: '110px', align: 'center', sortKey: 'commissionStatus',
      cell: (c) => {
        const m = COMMISSION_STATUS_META[c.commissionStatus] ?? COMMISSION_STATUS_META.cancelado;
        return (
          <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full border"
            style={{ background: `${m.color}18`, borderColor: `${m.color}44`, color: m.color }}>
            {m.label}
          </span>
        );
      } },
    { key: 'createdAt', label: 'Data', width: '160px', sortKey: 'createdAt',
      cell: (c) => <span className="text-[11px] text-white/55 font-mono">{dt(c.createdAt)}</span> },
    { key: 'actions', label: 'Ações', width: '60px', align: 'right', mobileHidden: true,
      cell: (c) => actions(c) },
  ];

  const actions = (c: StreamerCommission) =>
    c.commissionStatus === 'pendente' ? (
      <RowActions
        actions={[{
          label: 'Marcar Pago', icon: <CheckCircle2 size={13} />, variant: 'success',
          onClick: () => onPay(c), disabled: busyId === c.id,
        }]}
      />
    ) : <span className="text-white/25 text-xs">—</span>;

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div>
      {/* Totais */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
        <TotalCard color={COMMISSION_STATUS_META.pago.color} icon={<CheckCircle2 size={18} />}
          label="Pago" value={brl(totals.paid)} />
        <TotalCard color={COMMISSION_STATUS_META.pendente.color} icon={<Clock size={18} />}
          label="Pendente" value={brl(totals.pending)} />
        <TotalCard color={COMMISSION_STATUS_META.cancelado.color} icon={<X size={18} />}
          label="Cancelado" value={brl(totals.cancelled)} />
      </div>

      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex gap-1 surface-card rounded-lg p-1">
          {(['', 'pendente', 'pago', 'cancelado'] as const).map(st => (
            <button key={st || 'all'}
              onClick={() => { setStatus(st); setPage(0); }}
              className="px-3 py-1.5 rounded-md text-xs font-bold transition cursor-pointer"
              style={status === st
                ? { background: `${NEON.purple}22`, color: '#c9a9ff' }
                : { color: 'rgba(255,255,255,0.55)' }}>
              {st === '' ? 'Todas' : COMMISSION_STATUS_META[st].label}
            </button>
          ))}
        </div>
        <button onClick={fetchData} disabled={loading}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold
                     bg-white/[0.04] hover:bg-white/[0.08] text-white/75 transition cursor-pointer">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {error && <ErrorBox msg={error} />}

      <DataTable<StreamerCommission>
        columns={cols}
        rows={sorted}
        sort={sort}
        onSort={toggle}
        rowKey={(c) => c.id}
        loading={loading}
        empty="Nenhuma comissão"
        mobileTitle={(c) => c.nick || `#${c.streamerId}`}
        mobileSubtitle={(c) => `${brl(c.commissionBrl)} de ${brl(c.brl)}`}
        mobileActions={(c) => actions(c)}
      />

      <Pager page={page} totalPages={totalPages} loading={loading} onPage={setPage} />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════ SESSÕES ═════════ */

function SessionsTab() {
  const [items, setItems] = useState<StreamerSession[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { sorted, sort, toggle } = useTableSort(items, { key: 'startAt', order: 'desc' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listStreamerSessions({ skip: page * PAGE_SIZE, limit: PAGE_SIZE });
      setItems(res.items);
      setTotal(res.total);
    } catch (err: any) {
      setError(err?.detail || err?.message || 'Erro');
    } finally { setLoading(false); }
  }, [page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const cols: DataTableColumn<StreamerSession>[] = [
    { key: 'nick', label: 'Streamer', width: '1.4fr', sortKey: 'nick',
      cell: (s) => <span className="text-sm font-bold text-white truncate">{s.nick || `#${s.streamerId}`}</span> },
    { key: 'platform', label: 'Plataforma', width: '110px', sortKey: 'platform',
      cell: (s) => (
        <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded border"
          style={{ background: `${NEON.purple}14`, borderColor: `${NEON.purple}33`, color: '#c9a9ff' }}>
          {s.platform || '—'}
        </span>
      ) },
    { key: 'startAt', label: 'Início', width: '150px', sortKey: 'startAt',
      cell: (s) => <span className="text-[11px] text-white/70 font-mono">{dt(s.startAt)}</span> },
    { key: 'endAt', label: 'Fim', width: '150px', sortKey: 'endAt',
      cell: (s) => <span className="text-[11px] text-white/55 font-mono">{dt(s.endAt)}</span> },
    { key: 'durationSec', label: 'Duração', width: '100px', align: 'center', sortKey: 'durationSec',
      cell: (s) => <span className="text-xs text-white/85 font-mono">{fmtDuration(s.durationSec)}</span> },
    { key: 'validated', label: 'Validada', width: '100px', align: 'center', sortKey: 'validated',
      cell: (s) => (
        <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full border"
          style={s.validated
            ? { background: '#10b98118', borderColor: '#10b98144', color: '#10b981' }
            : { background: 'rgba(255,255,255,0.04)', borderColor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.45)' }}>
          {s.validated ? 'Válida' : 'Inválida'}
        </span>
      ) },
    { key: 'spendBrl', label: 'Gasto', width: '110px', align: 'right', sortKey: 'spendBrl',
      cell: (s) => <span className="text-sm font-mono" style={{ color: NEON.cyan }}>{brl(s.spendBrl)}</span> },
  ];

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-white/65">{total.toLocaleString('pt-BR')} sessão(ões) live</p>
        <button onClick={fetchData} disabled={loading}
          className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold
                     bg-white/[0.04] hover:bg-white/[0.08] text-white/75 transition cursor-pointer">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {error && <ErrorBox msg={error} />}

      <DataTable<StreamerSession>
        columns={cols}
        rows={sorted}
        sort={sort}
        onSort={toggle}
        rowKey={(s) => s.id}
        loading={loading}
        empty="Nenhuma sessão registrada"
        mobileTitle={(s) => (
          <span className="flex items-center gap-2">
            {s.nick || `#${s.streamerId}`}
            {s.validated && <Check size={13} className="text-emerald-400" />}
          </span>
        )}
        mobileSubtitle={(s) => `${s.platform || '—'} · ${fmtDuration(s.durationSec)} · ${brl(s.spendBrl)}`}
      />

      <Pager page={page} totalPages={totalPages} loading={loading} onPage={setPage} />
    </div>
  );
}

/* ═══════════════════════════════════════════════════════ CUPONS ══════════ */

const LIVE_TIERS = [
  { tier: 1, range: 'R$ 0 – R$ 99', pct: '10%' },
  { tier: 2, range: 'R$ 100 – R$ 249', pct: '12%' },
  { tier: 3, range: 'R$ 250 – R$ 499', pct: '15%' },
  { tier: 4, range: 'R$ 500 – R$ 999', pct: '18%' },
  { tier: 5, range: 'R$ 1.000 – R$ 1.999', pct: '20%' },
  { tier: 6, range: 'R$ 2.000 – R$ 4.999', pct: '22%' },
  { tier: 7, range: 'R$ 5.000+', pct: '25%' },
];

const COUPON_TYPE_META: Record<string, { label: string; color: string }> = {
  fixo: { label: 'Fixo', color: NEON.purple },
  live: { label: 'Live', color: '#fa3a3a' },
};

function CouponStatusBadge({ c }: { c: StreamerCouponRow }) {
  const meta = c.active
    ? { label: 'Ativo', color: '#10b981' }
    : c.expired
      ? { label: 'Expirado', color: '#8a94a3' }
      : { label: 'Inativo', color: '#8a94a3' };
  return (
    <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full border"
      style={{ background: `${meta.color}18`, borderColor: `${meta.color}44`, color: meta.color }}>
      {meta.label}
    </span>
  );
}

function CouponsTab() {
  const { notify } = useToast();
  const [items, setItems] = useState<StreamerCouponRow[]>([]);
  const [total, setTotal] = useState(0);
  const [type, setType] = useState<'' | 'fixo' | 'live'>('');
  const [onlyActive, setOnlyActive] = useState(false);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showRules, setShowRules] = useState(false);

  const { sorted, sort, toggle } = useTableSort(items, { key: 'createdAt', order: 'desc' });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await listStreamerCoupons({
        type: type || undefined,
        onlyActive: onlyActive || undefined,
        skip: page * PAGE_SIZE,
        limit: PAGE_SIZE,
      });
      setItems(res.items);
      setTotal(res.total);
    } catch (err: any) {
      setError(err?.detail || err?.message || 'Erro');
    } finally { setLoading(false); }
  }, [type, onlyActive, page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const copyCode = async (code: string) => {
    try { await navigator.clipboard.writeText(code); notify('success', `Cupom ${code} copiado`); }
    catch { notify('error', 'Não foi possível copiar'); }
  };

  const cols: DataTableColumn<StreamerCouponRow>[] = [
    { key: 'code', label: 'Código', width: '1.4fr', sortKey: 'code',
      cell: (c) => (
        <button onClick={() => copyCode(c.code)}
          className="group flex items-center gap-1.5 font-mono text-sm font-bold text-white
                     px-2 py-1 rounded-md border transition cursor-pointer"
          style={{ background: `${NEON.purple}12`, borderColor: `${NEON.purple}33` }}
          title="Copiar código">
          <Ticket size={13} style={{ color: '#c9a9ff' }} />
          {c.code}
          <Copy size={12} className="opacity-40 group-hover:opacity-90" />
        </button>
      ) },
    { key: 'streamerNick', label: 'Streamer', width: '1.2fr', sortKey: 'streamerNick',
      cell: (c) => (
        <div className="min-w-0">
          <p className="text-sm font-bold text-white truncate">{c.streamerNick || `#${c.streamerId}`}</p>
          <p className="text-[10px] text-white/40 truncate">{c.accountId || '—'}</p>
        </div>
      ) },
    { key: 'type', label: 'Tipo', width: '90px', align: 'center', sortKey: 'type',
      cell: (c) => {
        const m = COUPON_TYPE_META[String(c.type)] ?? { label: c.type || '—', color: '#8a94a3' };
        return (
          <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded border"
            style={{ background: `${m.color}18`, borderColor: `${m.color}44`, color: m.color }}>
            {m.label}
          </span>
        );
      } },
    { key: 'cashbackPct', label: 'Cashback', width: '95px', align: 'center', sortKey: 'cashbackPct',
      cell: (c) => <span className="text-sm font-bold" style={{ color: NEON.magenta }}>{c.cashbackPct ?? 0}%</span> },
    { key: 'active', label: 'Status', width: '105px', align: 'center', sortKey: 'active',
      cell: (c) => <CouponStatusBadge c={c} /> },
    { key: 'usesCount', label: 'Usos', width: '70px', align: 'center', sortKey: 'usesCount',
      cell: (c) => <span className="text-sm text-white/80 font-mono">{c.usesCount}</span> },
    { key: 'createdAt', label: 'Criado', width: '150px', sortKey: 'createdAt',
      cell: (c) => <span className="text-[11px] text-white/55 font-mono">{dt(c.createdAt)}</span> },
    { key: 'expiresAt', label: 'Expira', width: '150px', sortKey: 'expiresAt',
      cell: (c) => <span className="text-[11px] text-white/55 font-mono">{dt(c.expiresAt)}</span> },
  ];

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <div>
      {/* Onde nascem os cupons */}
      <div className="surface-card rounded-lg p-4 mb-5 flex items-start gap-3 border"
        style={{ borderColor: `${NEON.cyan}33` }}>
        <Info size={18} className="shrink-0 mt-0.5" style={{ color: NEON.cyan }} />
        <p className="text-xs text-white/70 leading-relaxed">
          Os cupons são <b className="text-white/90">gerados pelo próprio streamer</b> no painel dele
          (<b className="text-white/90">Fixo</b>: 5% / 5 dias, sob demanda; <b className="text-white/90">Live</b>:
          criado automaticamente ao iniciar uma sessão live). Aqui a equipe <b className="text-white/90">vê e audita</b> todos
          os cupons — código, streamer, tipo, validade e quantos usos já teve. As regras (faixas, %) estão em “Ver regras”.
        </p>
      </div>

      {/* Filtros */}
      <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
        <div className="flex gap-1 surface-card rounded-lg p-1">
          {(['', 'fixo', 'live'] as const).map(t => (
            <button key={t || 'all'}
              onClick={() => { setType(t); setPage(0); }}
              className="px-3 py-1.5 rounded-md text-xs font-bold transition cursor-pointer"
              style={type === t
                ? { background: `${NEON.purple}22`, color: '#c9a9ff' }
                : { color: 'rgba(255,255,255,0.55)' }}>
              {t === '' ? 'Todos' : COUPON_TYPE_META[t].label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => { setOnlyActive(v => !v); setPage(0); }}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-bold transition cursor-pointer border"
            style={onlyActive
              ? { background: '#10b98118', borderColor: '#10b98144', color: '#10b981' }
              : { background: 'rgba(255,255,255,0.04)', borderColor: 'rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.6)' }}>
            <CheckCircle2 size={14} /> Só ativos
          </button>
          <button onClick={fetchData} disabled={loading}
            className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold
                       bg-white/[0.04] hover:bg-white/[0.08] text-white/75 transition cursor-pointer">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {error && <ErrorBox msg={error} />}

      <DataTable<StreamerCouponRow>
        columns={cols}
        rows={sorted}
        sort={sort}
        onSort={toggle}
        rowKey={(c) => c.id}
        loading={loading}
        empty="Nenhum cupom gerado ainda — os streamers geram no painel deles."
        mobileTitle={(c) => (
          <span className="flex items-center gap-2 font-mono">
            {c.code} <CouponStatusBadge c={c} />
          </span>
        )}
        mobileSubtitle={(c) => `${c.streamerNick || `#${c.streamerId}`} · ${c.cashbackPct ?? 0}% · ${c.usesCount} usos`}
      />

      <Pager page={page} totalPages={totalPages} loading={loading} onPage={setPage} />

      {/* Regras (recolhível) */}
      <button onClick={() => setShowRules(v => !v)}
        className="flex items-center gap-2 mt-6 mb-3 text-sm font-bold text-white/80 hover:text-white cursor-pointer">
        <ChevronDown size={16} className={`transition-transform ${showRules ? '' : '-rotate-90'}`} />
        Regras dos cupons (faixas, cashback, integridade)
      </button>
      {showRules && <CouponRules />}
    </div>
  );
}

function CouponRules() {
  return (
    <div className="space-y-4 max-w-3xl">
      {/* Cupom Fixo */}
      <div className="surface-card rounded-lg p-5 border" style={{ borderColor: `${NEON.purple}33` }}>
        <div className="flex items-center gap-2 mb-2">
          <span className="w-8 h-8 rounded-lg flex items-center justify-center border"
            style={{ background: `${NEON.purple}18`, borderColor: `${NEON.purple}44`, color: '#c9a9ff' }}>
            <Ticket size={16} />
          </span>
          <h3 className="text-base font-bold text-white">Cupom Fixo — 5% Cashback</h3>
        </div>
        <ul className="text-sm text-white/70 space-y-1.5 leading-relaxed list-disc pl-5">
          <li>Gerado <b className="text-white/90">manualmente</b> pelo próprio streamer no painel dele.</li>
          <li>Válido por <b className="text-white/90">5 dias corridos</b> a partir da geração; ao expirar cai no histórico e precisa gerar um novo.</li>
          <li>Cashback fixo de <b style={{ color: NEON.magenta }}>5%</b> em qualquer recarga que use o cupom.</li>
          <li>Comissão de 5% do streamer sobre o valor bruto (Fixo ou Live).</li>
        </ul>
      </div>

      {/* Cupom Live */}
      <div className="surface-card rounded-lg p-5 border" style={{ borderColor: '#fa3a3a33' }}>
        <div className="flex items-center gap-2 mb-3">
          <span className="w-8 h-8 rounded-lg flex items-center justify-center border"
            style={{ background: '#fa3a3a18', borderColor: '#fa3a3a44', color: '#ff5a5a' }}>
            <Radio size={16} />
          </span>
          <h3 className="text-base font-bold text-white">Cupom Live — Cashback Progressivo (10–25%)</h3>
        </div>
        <p className="text-sm text-white/70 leading-relaxed mb-3">
          Temporário — ativo somente durante a live verificada. Libera após <b className="text-white/90">30 minutos</b> de
          sessão contínua. Um cupom Live ativo por vez por streamer. O cashback sobe por faixa de gasto acumulado na sessão:
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-wider text-white/45 text-left">
                <th className="py-2 pr-4 font-bold">Tier</th>
                <th className="py-2 pr-4 font-bold">Gasto acumulado (na live)</th>
                <th className="py-2 font-bold text-right">Cashback</th>
              </tr>
            </thead>
            <tbody>
              {LIVE_TIERS.map(t => (
                <tr key={t.tier} className="border-t border-white/[0.05]">
                  <td className="py-2 pr-4 text-white/60 font-mono">{t.tier}</td>
                  <td className="py-2 pr-4 text-white/85">{t.range}</td>
                  <td className="py-2 text-right font-bold" style={{ color: NEON.magenta }}>{t.pct}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Regras / anti-burla */}
      <div className="surface-card rounded-lg p-5 border" style={{ borderColor: `${NEON.cyan}33` }}>
        <div className="flex items-center gap-2 mb-2">
          <span className="w-8 h-8 rounded-lg flex items-center justify-center border"
            style={{ background: `${NEON.cyan}14`, borderColor: `${NEON.cyan}33`, color: NEON.cyan }}>
            <Check size={16} />
          </span>
          <h3 className="text-base font-bold text-white">Regras de integridade</h3>
        </div>
        <ul className="text-sm text-white/70 space-y-1.5 leading-relaxed list-disc pl-5">
          <li>O Cupom Live só ativa com <b className="text-white/90">live verificável</b> no canal cadastrado (via API).</li>
          <li>Tempo mínimo de <b className="text-white/90">30 minutos</b> de sessão para contar e liberar o Cupom Live.</li>
          <li>A verificação dos 30 min e a liberação acontecem <b className="text-white/90">na API</b>, sem job/rotina — checagem sob demanda, sem o front liberar sozinho.</li>
          <li>Alteração de canal vinculado requer aprovação manual da equipe; auditoria mensal antes do pagamento.</li>
        </ul>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════ CREDENCIAIS ═════ */

const PLATFORMS: { key: StreamerPlatform; label: string; color: string; needsKey: boolean; note: string }[] = [
  { key: 'twitch', label: 'Twitch', color: NEON.purple, needsKey: true,
    note: 'Client ID + Client Secret melhoram precisão e contagem de viewers.' },
  { key: 'youtube', label: 'YouTube', color: '#ff0033', needsKey: true,
    note: 'API Key (YouTube Data) melhora precisão e viewers em tempo real.' },
  { key: 'kick', label: 'Kick', color: '#53fc18', needsKey: false,
    note: 'Sem chave necessária — detecção best-effort via yt-dlp.' },
  { key: 'tiktok', label: 'TikTok', color: '#25f4ee', needsKey: false,
    note: 'Sem chave necessária — detecção best-effort.' },
];

function CredentialsTab() {
  const { notify } = useToast();
  const [creds, setCreds] = useState<StreamerCredential[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try { setCreds(await listStreamerCredentials()); }
    catch (err: any) { setError(err?.detail || err?.message || 'Erro'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  return (
    <div>
      <div className="surface-card rounded-lg p-4 mb-5 flex items-start gap-3 border"
        style={{ borderColor: `${NEON.cyan}33` }}>
        <AlertCircle size={18} className="shrink-0 mt-0.5" style={{ color: NEON.cyan }} />
        <p className="text-xs text-white/70 leading-relaxed">
          Sem chave, o sistema já detecta live via <b className="text-white/90">yt-dlp</b>. A chave (Twitch / YouTube)
          <b className="text-white/90"> melhora a precisão</b> e a contagem de viewers. Kick e TikTok não precisam de chave — funcionam em modo best-effort.
        </p>
      </div>

      {error && <ErrorBox msg={error} />}

      {loading ? (
        <div className="py-16 flex items-center justify-center text-white/40">
          <Loader2 size={20} className="animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {PLATFORMS.map(p => (
            <CredentialCard
              key={p.key}
              meta={p}
              cred={creds.find(c => c.platform === p.key) ?? null}
              onSaved={fetchData}
              notify={notify}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function CredentialCard({ meta, cred, onSaved, notify }: {
  meta: { key: StreamerPlatform; label: string; color: string; needsKey: boolean; note: string };
  cred: StreamerCredential | null;
  onSaved: () => Promise<void>;
  notify: (kind: 'success' | 'error', msg: string) => void;
}) {
  const [enabled, setEnabled] = useState(cred?.enabled ?? false);
  const [clientId, setClientId] = useState(cred?.clientId ?? '');
  const [clientSecret, setClientSecret] = useState('');
  const [apiKey, setApiKey] = useState('');
  const [showSecret, setShowSecret] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [busy, setBusy] = useState(false);

  // Twitch usa clientId + clientSecret; YouTube usa apiKey (e opcional clientId).
  const usesSecret = meta.key === 'twitch';
  const usesApiKey = meta.key === 'youtube';

  const save = async () => {
    setBusy(true);
    try {
      await updateStreamerCredential(meta.key, {
        clientId: clientId.trim() || undefined,
        ...(clientSecret ? { clientSecret } : {}),
        ...(apiKey ? { apiKey } : {}),
        enabled,
      });
      notify('success', `${meta.label} atualizado`);
      setClientSecret('');
      setApiKey('');
      await onSaved();
    } catch (err: any) {
      notify('error', err?.detail || err?.message || 'Erro');
    } finally { setBusy(false); }
  };

  return (
    <div className="surface-card rounded-lg p-5 border" style={{ borderColor: `${meta.color}33` }}>
      <div className="flex items-center justify-between mb-3 pb-3 border-b border-white/[0.06]">
        <div className="flex items-center gap-2.5">
          <span className="w-9 h-9 rounded-lg flex items-center justify-center border"
            style={{ background: `${meta.color}18`, borderColor: `${meta.color}44`, color: meta.color }}>
            <Radio size={16} />
          </span>
          <div>
            <h3 className="text-base font-bold text-white">{meta.label}</h3>
            <p className="text-[10px] text-white/45">
              {cred?.updatedAt ? `atualizado ${new Date(cred.updatedAt).toLocaleDateString('pt-BR')}` : 'não configurado'}
            </p>
          </div>
        </div>
        <Toggle value={enabled} onChange={setEnabled} color={meta.color} />
      </div>

      {meta.needsKey ? (
        <div className="space-y-3">
          {(usesSecret || meta.key === 'youtube') && (
            <div>
              <label className="text-[11px] text-white/55 mb-1 block">Client ID {usesApiKey && '(opcional)'}</label>
              <input type="text" value={clientId}
                onChange={(e) => setClientId(e.target.value)}
                className="w-full bg-[#0d141b] border border-white/[0.08] rounded-lg px-3 py-2 text-sm text-white font-mono
                           outline-none focus:border-white/20" />
            </div>
          )}
          {usesSecret && (
            <SecretField label="Client Secret" value={clientSecret} onChange={setClientSecret}
              show={showSecret} onToggleShow={() => setShowSecret(v => !v)}
              placeholder={cred?.hasClientSecret ? '••••••• (mantém o atual se vazio)' : 'cole o secret'} />
          )}
          {usesApiKey && (
            <SecretField label="API Key" value={apiKey} onChange={setApiKey}
              show={showKey} onToggleShow={() => setShowKey(v => !v)}
              placeholder={cred?.hasApiKey ? '••••••• (mantém o atual se vazio)' : 'cole a API key'} />
          )}
        </div>
      ) : (
        <p className="text-xs text-white/45 leading-relaxed py-1">{meta.note}</p>
      )}

      {meta.needsKey && <p className="text-[10px] text-white/40 mt-2 leading-relaxed">{meta.note}</p>}

      <button onClick={save} disabled={busy}
        className="w-full mt-4 px-4 py-2.5 rounded-lg font-bold text-sm transition cursor-pointer
                   disabled:opacity-50 flex items-center justify-center gap-2 text-white"
        style={{ background: `${meta.color}22`, border: `1px solid ${meta.color}55` }}>
        {busy && <Loader2 size={14} className="animate-spin" />}
        Salvar {meta.label}
      </button>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════ shared UI ═══════ */

function NeonButton({ children, onClick, disabled, className = '' }: {
  children: React.ReactNode; onClick: () => void; disabled?: boolean; className?: string;
}) {
  return (
    <button onClick={onClick} disabled={disabled}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-bold text-white transition cursor-pointer
                  disabled:opacity-50 ${className}`}
      style={{
        background: `linear-gradient(135deg, ${NEON.purple}, ${NEON.magenta})`,
        boxShadow: `0 0 18px ${NEON.purple}44`,
      }}>
      {children}
    </button>
  );
}

function NeonModal({ children, onClose, maxW = '600px', title, icon }: {
  children: React.ReactNode; onClose: () => void; maxW?: string;
  title: string; icon?: React.ReactNode;
}) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div onClick={(e) => e.stopPropagation()}
        className="surface-card rounded-lg w-full p-6 max-h-[92vh] overflow-y-auto border"
        style={{ maxWidth: maxW, borderColor: `${NEON.purple}44`, boxShadow: `0 0 40px ${NEON.purple}22` }}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-white flex items-center gap-2">
            <span style={{ color: '#c9a9ff' }}>{icon}</span>
            {title}
          </h2>
          <button onClick={onClose} className="text-white/55 hover:text-white cursor-pointer">
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}

function TotalCard({ color, icon, label, value }: {
  color: string; icon: React.ReactNode; label: string; value: string;
}) {
  return (
    <div className="surface-card rounded-lg p-4 border" style={{ borderColor: `${color}22` }}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[10px] font-bold text-white/55 uppercase tracking-wider">{label}</p>
          <p className="text-xl font-black text-white mt-1 leading-none">{value}</p>
        </div>
        <div className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 border"
          style={{ background: `${color}15`, borderColor: `${color}30`, color }}>
          {icon}
        </div>
      </div>
    </div>
  );
}

function Toggle({ value, onChange, color }: {
  value: boolean; onChange: (v: boolean) => void; color: string;
}) {
  return (
    <button type="button" role="switch" aria-checked={value} onClick={() => onChange(!value)}
      className="relative w-12 h-6 rounded-full transition cursor-pointer"
      style={{ background: value ? color : 'rgba(255,255,255,0.10)' }}>
      <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full transition-all ${value ? 'left-6' : 'left-0.5'}`} />
    </button>
  );
}

function SecretField({ label, value, onChange, show, onToggleShow, placeholder }: {
  label: string; value: string; onChange: (v: string) => void;
  show: boolean; onToggleShow: () => void; placeholder: string;
}) {
  return (
    <div>
      <label className="text-[11px] text-white/55 mb-1 block">{label}</label>
      <div className="relative">
        <input type={show ? 'text' : 'password'} value={value} placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          className="w-full bg-[#0d141b] border border-white/[0.08] rounded-lg px-3 py-2 pr-10 text-sm text-white font-mono
                     outline-none focus:border-white/20" />
        <button type="button" onClick={onToggleShow}
          className="absolute right-2 top-1/2 -translate-y-1/2 text-white/55 hover:text-white p-1 cursor-pointer"
          title={show ? 'Ocultar' : 'Mostrar'}>
          {show ? <EyeOff size={14} /> : <Eye size={14} />}
        </button>
      </div>
    </div>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-white/[0.06] bg-white/[0.02] p-4">
      <p className="text-[11px] font-black uppercase tracking-[0.15em] text-white/55 mb-2.5">{title}</p>
      {children}
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div className="text-xs text-white/40 py-2">{children}</div>;
}

function ErrorBox({ msg }: { msg: string }) {
  return (
    <div className="surface-card rounded-lg p-4 mb-4 flex items-start gap-3 border-red-500/30">
      <AlertCircle size={18} className="text-red-400 shrink-0 mt-0.5" />
      <p className="text-sm text-red-400">{msg}</p>
    </div>
  );
}

function Pager({ page, totalPages, loading, onPage }: {
  page: number; totalPages: number; loading: boolean; onPage: (p: number) => void;
}) {
  return (
    <div className="flex items-center justify-between mt-4 text-xs text-white/55">
      <span>Página {page + 1} de {totalPages}</span>
      <div className="flex gap-2">
        <button onClick={() => page > 0 && onPage(page - 1)} disabled={page === 0 || loading}
          className="px-3 py-1.5 rounded-lg bg-[#161e26] border border-white/[0.06]
                     hover:border-[#9146FF]/40 disabled:opacity-40 disabled:cursor-not-allowed
                     transition cursor-pointer">
          Anterior
        </button>
        <button onClick={() => page + 1 < totalPages && onPage(page + 1)} disabled={page + 1 >= totalPages || loading}
          className="px-3 py-1.5 rounded-lg bg-[#161e26] border border-white/[0.06]
                     hover:border-[#9146FF]/40 disabled:opacity-40 disabled:cursor-not-allowed
                     transition cursor-pointer">
          Próxima
        </button>
      </div>
    </div>
  );
}
